import { SettingCard } from "@/app/Settings/Settings/Components/SettingFormCard";
import {
  EditableRowsProvider,
  EditableSettingRow,
  SettingRowsCard,
} from "@/app/Settings/Settings/Components/EditableSettingRow";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { useSettingsSupplier } from "@/app/Suppliers/Utils/useSettingsSupplier";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { updateContactMutation } from "@/app/Contacts/GraphQL/contactMutations";
import { PhoneInput } from "@/lib/Components/Form/Inputs/PhoneInput";
import {
  InvoiceStatus,
  PaymentStatus,
  Region,
  TaxType,
  UpdateContactInput,
} from "@/gql/graphql";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatOrdinal } from "@/lib/Formatters/formatOrdinal";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  InformationCircleIcon,
  ClockIcon,
  DocumentTextIcon,
} from "@heroicons/react/20/solid";
import {
  AddPaymentMethodButton,
  StripePaymentMethodCard,
} from "@/app/Stripe/StripePaymentMethods/Components/StripePaymentMethodCard";
import { PaymentTable } from "@/app/Payments/Components/PaymentTable";
import { InvoiceTable } from "@/app/Invoices/Components/InvoiceTable";
import { Banknote, Fuel, LucideIcon, ShieldCheck, Ship } from "lucide-react";
import { cn } from "@/lib/utils";
import { ComponentType, SVGProps, useState } from "react";
import { BillingCycleDateInput } from "@/app/Contacts/Components/BillingCycleDateInput";
import { useTranslations } from "use-intl";
import { billingEntitiesQuery } from "@/app/Settings/Settings/GraphQL/billingEntitiesQuery";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Select } from "@/lib/Components/Form/Components/Select";
import { useContactActions } from "@/app/Contacts/Hooks/useContactActions";
import { ActionGroupButtons } from "@/lib/Components/Actions/ActionGroupButtons";
import { AdminSection } from "@/lib/Components/Common/AdminSection";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import {
  SupplierBillingEntityForm,
  SupplierBookingFeeForm,
  SupplierHoldingDepositForm,
  SupplierFerryRefundForm,
  SupplierFuelRefundForm,
  SupplierInvoiceAmountForm,
} from "@/app/Suppliers/Forms/SupplierBillingForm";
import { updateSupplierMutation } from "@/app/Suppliers/GraphQL/supplierMutations";
import {
  supplierBookingFeeValidation,
  supplierInvoiceAmountValidation,
} from "@/app/Suppliers/Validation/supplierBillingValidation";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";

export function SettingsBillingScreen() {
  const { supplier } = useSettingsSupplier();
  const { mutateAsync } = useGqlMutation(updateContactMutation);
  const { mutateAsync: updateSupplier } = useGqlMutation(
    updateSupplierMutation,
  );
  const t = useTranslations("settings.billing");
  const { entityId } = useParams({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/settings/billing/$entityId",
  });
  const navigate = useNavigate();
  const getContactActions = useContactActions();
  const { isAdmin } = useTenant();

  const { data } = useGqlQuery(billingEntitiesQuery, {
    supplier_id: supplier?.id,
  });

  const billingEntities = data?.billingEntities ?? [];
  const defaultBillingEntity = supplier?.billingEntity;

  if (!supplier) {
    return null;
  }

  const selectedEntity =
    billingEntities.find((e) => e.id === entityId) ?? defaultBillingEntity;

  if (!selectedEntity) {
    return null;
  }

  const billingEntityOptions = billingEntities.map((entity) => ({
    value: entity.id,
    label: entity.name ?? entity.legal_name ?? "Unknown",
  }));

  const showEntitySelector = billingEntities.length > 1;

  const gstLabel =
    supplier.tax_type === TaxType.Gst
      ? supplier.is_tax_included
        ? t("billing.incGst")
        : t("billing.plusGst")
      : undefined;

  return (
    <EditableRowsProvider>
      <div className="space-y-8">
        <SettingCard
          title={t("billing.title")}
          description={t("billing.description")}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <BillingStatCard
              label={t("billing.costPerMove")}
              tooltip={t("billing.costPerMoveTooltip", {
                day: formatOrdinal(selectedEntity.billing_cycle_day_of_month),
              })}
              value={formatCurrency(
                supplier.charge_per_move_amount > 0
                  ? supplier.charge_per_move_amount
                  : supplier.holding_deposit_amount,
                supplier.billing_currency,
              )}
              suffix={gstLabel}
              icon={Banknote}
              accent="indigo"
            />

            <BillingStatCard
              label={t("billing.holdingDeposit")}
              tooltip={t("billing.holdingDepositTooltip")}
              value={
                supplier.is_holding_deposit_required
                  ? formatCurrency(
                      supplier.holding_deposit_amount,
                      supplier.billing_currency,
                    )
                  : t("billing.disabled")
              }
              icon={ShieldCheck}
              accent="amber"
              inactive={!supplier.is_holding_deposit_required}
            />

            <BillingStatCard
              label={t("billing.fuelRefund")}
              tooltip={t("billing.fuelRefundTooltip")}
              value={
                supplier.imoova_refunds_fuel
                  ? formatCurrency(supplier.fuel_refund_fee, supplier.currency)
                  : t("billing.supplierManaged")
              }
              icon={Fuel}
              accent="emerald"
              inactive={!supplier.imoova_refunds_fuel}
            />

            <BillingStatCard
              label={t("billing.ferryRefund")}
              tooltip={t("billing.ferryRefundTooltip")}
              value={
                supplier.imoova_refunds_ferry
                  ? formatCurrency(supplier.ferry_refund_fee, supplier.currency)
                  : t("billing.supplierManaged")
              }
              icon={Ship}
              accent="sky"
              inactive={!supplier.imoova_refunds_ferry}
            />
          </div>
        </SettingCard>

        {showEntitySelector ? (
          <SettingCard
            title={t("billingEntity.title")}
            description={t("billingEntity.description")}
            buttons={
              <ActionGroupButtons buttons={getContactActions(selectedEntity)} />
            }
          >
            <Select
              value={selectedEntity.id}
              onChange={(value) => {
                navigate({
                  from: "/$tenant/$orgId/supplier/{-$supplierId}",
                  to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/billing/$entityId",
                  params: (prev) => ({ ...prev, entityId: value }),
                  search: (prev) => prev,
                });
              }}
              options={billingEntityOptions}
              placeholder={t("billingEntity.selectPlaceholder")}
            />
          </SettingCard>
        ) : null}

        <SettingCard
          title={t("paymentMethods.title")}
          description={t("paymentMethods.description")}
          buttons={<AddPaymentMethodButton contact={selectedEntity} />}
        >
          <StripePaymentMethodCard contact={selectedEntity} />
        </SettingCard>

        <SettingRowsCard
          title={t("billingContact.title")}
          description={t("billingContact.description")}
        >
          {isAdmin ? (
            <EditableSettingRow
              label={t("billingContact.billingEntityLabel")}
              value={
                supplier.billingEntity?.name ??
                supplier.billingEntity?.legal_name
              }
              initialValues={{
                billingEntity: {
                  connect: supplier.billingEntity?.id ?? null!,
                },
              }}
              onSave={async (values) => {
                await updateSupplier({ id: supplier.id, input: values });
                await navigate({
                  from: "/$tenant/$orgId/supplier/{-$supplierId}",
                  to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/billing/$entityId",
                  params: (prev) => ({
                    ...prev,
                    entityId: values.billingEntity!.connect,
                  }),
                  search: (prev) => prev,
                });
              }}
            >
              <SupplierBillingEntityForm
                supplier={supplier}
                label={t("billingContact.billingEntityLabel")}
              />
            </EditableSettingRow>
          ) : null}

          <EditableSettingRow<UpdateContactInput>
            key={`${selectedEntity.id}-legal_name`}
            label={t("billingContact.legalNameLabel")}
            value={selectedEntity.legal_name}
            initialValues={{ legal_name: selectedEntity.legal_name }}
            onSave={(values) =>
              mutateAsync({ id: selectedEntity.id, input: values })
            }
          >
            <TextInput
              name={"legal_name"}
              label={t("billingContact.legalNameLabel")}
              tooltip={t("billingContact.legalNameTooltip")}
            />
          </EditableSettingRow>

          <EditableSettingRow<UpdateContactInput>
            key={`${selectedEntity.id}-billing_email`}
            label={t("billingContact.billingEmailLabel")}
            value={selectedEntity.billing_email}
            initialValues={{ billing_email: selectedEntity.billing_email }}
            onSave={(values) =>
              mutateAsync({ id: selectedEntity.id, input: values })
            }
          >
            <TextInput
              name="billing_email"
              label={t("billingContact.billingEmailLabel")}
            />
          </EditableSettingRow>

          <EditableSettingRow<UpdateContactInput>
            key={`${selectedEntity.id}-billing_phone`}
            label={t("billingContact.billingPhoneLabel")}
            value={selectedEntity.billing_phone}
            initialValues={{ billing_phone: selectedEntity.billing_phone }}
            onSave={(values) =>
              mutateAsync({ id: selectedEntity.id, input: values })
            }
          >
            <PhoneInput
              name="billing_phone"
              label={t("billingContact.billingPhoneLabel")}
              defaultRegion={Region.Au}
            />
          </EditableSettingRow>

          <EditableSettingRow<UpdateContactInput>
            key={`${selectedEntity.id}-company_number`}
            label={t("billingContact.businessRegistrationLabel")}
            value={selectedEntity.company_number}
            initialValues={{ company_number: selectedEntity.company_number }}
            onSave={(values) =>
              mutateAsync({ id: selectedEntity.id, input: values })
            }
          >
            <TextInput
              name={"company_number"}
              label={t("billingContact.businessRegistrationLabel")}
              tooltip={t("billingContact.businessRegistrationTooltip")}
            />
          </EditableSettingRow>

          <EditableSettingRow<UpdateContactInput>
            key={`${selectedEntity.id}-purchase_order_number`}
            label={t("billingContact.defaultPoLabel")}
            value={selectedEntity.purchase_order_number}
            initialValues={{
              purchase_order_number: selectedEntity.purchase_order_number,
            }}
            onSave={(values) =>
              mutateAsync({ id: selectedEntity.id, input: values })
            }
          >
            <TextInput
              name={"purchase_order_number"}
              label={t("billingContact.defaultPoLabel")}
              optionalLabel
            />
          </EditableSettingRow>

          <EditableSettingRow<UpdateContactInput>
            key={`${selectedEntity.id}-billing_cycle_day_of_month`}
            label={t("billingContact.invoiceCycleDateLabel")}
            value={formatOrdinal(selectedEntity.billing_cycle_day_of_month)}
            initialValues={{
              billing_cycle_day_of_month:
                selectedEntity.billing_cycle_day_of_month,
            }}
            onSave={(values) =>
              mutateAsync({ id: selectedEntity.id, input: values })
            }
          >
            <BillingCycleDateInput
              label={t("billingContact.invoiceCycleDateLabel")}
              name="billing_cycle_day_of_month"
            />
          </EditableSettingRow>
        </SettingRowsCard>

        <BillingActivityCard entityId={selectedEntity.id} />

        <AdminSection containerClassName="bg-white">
          <SettingRowsCard
            title="Billing (Imoova)"
            description="What Imoova charges this supplier, and how their invoices are built."
          >
            <EditableSettingRow
              label="Invoice amount"
              value={`${formatCurrency(
                supplier.charge_per_move_amount,
                supplier.billing_currency,
              )}${gstLabel ? ` ${gstLabel}` : ""} per move`}
              editClassName="max-w-2xl"
              validationSchema={toFormikValidationSchema(
                supplierInvoiceAmountValidation,
              )}
              initialValues={{
                charge_per_move_amount: supplier.charge_per_move_amount,
                billing_currency: supplier.billing_currency,
                is_tax_included: supplier.is_tax_included,
                tax_type: supplier.tax_type,
                confirm_delivery_before_billing:
                  supplier.confirm_delivery_before_billing,
              }}
              onSave={(values) =>
                updateSupplier({ id: supplier.id, input: values })
              }
            >
              <SupplierInvoiceAmountForm supplier={supplier} />
            </EditableSettingRow>

            <EditableSettingRow
              label="Holding deposit"
              value={
                supplier.is_holding_deposit_required
                  ? formatCurrency(
                      supplier.holding_deposit_amount,
                      supplier.currency,
                    )
                  : "Not required"
              }
              editClassName="max-w-2xl"
              initialValues={{
                is_holding_deposit_required:
                  supplier.is_holding_deposit_required,
                holding_deposit_amount: supplier.holding_deposit_amount,
                holding_deposit_refunded_at:
                  supplier.holding_deposit_refunded_at,
                holding_deposit_refunded_by:
                  supplier.holding_deposit_refunded_by,
              }}
              onSave={(values) =>
                updateSupplier({ id: supplier.id, input: values })
              }
            >
              <SupplierHoldingDepositForm supplier={supplier} />
            </EditableSettingRow>

            <EditableSettingRow
              label="Booking fee"
              value={formatCurrency(
                supplier.booking_fee_amount,
                supplier.currency,
              )}
              editClassName="max-w-2xl"
              validationSchema={toFormikValidationSchema(
                supplierBookingFeeValidation,
              )}
              initialValues={{
                booking_fee_amount: supplier.booking_fee_amount,
              }}
              onSave={(values) =>
                updateSupplier({ id: supplier.id, input: values })
              }
            >
              <SupplierBookingFeeForm supplier={supplier} />
            </EditableSettingRow>

            <EditableSettingRow
              label="Fuel refunds"
              value={
                supplier.imoova_refunds_fuel
                  ? formatCurrency(supplier.fuel_refund_fee, supplier.currency)
                  : "Not enabled"
              }
              editClassName="max-w-2xl"
              initialValues={{
                imoova_refunds_fuel: supplier.imoova_refunds_fuel,
                fuel_refund_fee: supplier.fuel_refund_fee,
              }}
              onSave={(values) =>
                updateSupplier({ id: supplier.id, input: values })
              }
            >
              <SupplierFuelRefundForm supplier={supplier} />
            </EditableSettingRow>

            <EditableSettingRow
              label="Ferry refunds"
              value={
                supplier.imoova_refunds_ferry
                  ? formatCurrency(supplier.ferry_refund_fee, supplier.currency)
                  : "Not enabled"
              }
              editClassName="max-w-2xl"
              initialValues={{
                imoova_refunds_ferry: supplier.imoova_refunds_ferry,
                ferry_refund_fee: supplier.ferry_refund_fee,
              }}
              onSave={(values) =>
                updateSupplier({ id: supplier.id, input: values })
              }
            >
              <SupplierFerryRefundForm supplier={supplier} />
            </EditableSettingRow>
          </SettingRowsCard>
        </AdminSection>
      </div>
    </EditableRowsProvider>
  );
}

const statAccents = {
  indigo: "bg-indigo-50 text-indigo-600",
  amber: "bg-amber-50 text-amber-600",
  emerald: "bg-emerald-50 text-emerald-600",
  sky: "bg-sky-50 text-sky-600",
};

function BillingStatCard({
  label,
  tooltip,
  value,
  suffix,
  icon: Icon,
  accent,
  inactive = false,
}: {
  label: string;
  tooltip: string;
  value: string;
  suffix?: string;
  icon: LucideIcon;
  accent: keyof typeof statAccents;
  inactive?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
      <span
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-lg",
          inactive ? "bg-gray-100 text-gray-400" : statAccents[accent],
        )}
      >
        <Icon className="size-5" />
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {label}
          </p>
          <Tooltip>
            <TooltipTrigger>
              <InformationCircleIcon className="size-4 text-gray-300 transition-colors hover:text-gray-500" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
          </Tooltip>
        </div>
        <p
          className={cn(
            "mt-1 truncate text-xl font-semibold",
            inactive ? "text-gray-400" : "text-gray-900",
          )}
        >
          {value}
          {suffix ? (
            <span className="ml-1.5 text-sm font-medium text-gray-500">
              {suffix}
            </span>
          ) : null}
        </p>
      </div>
    </div>
  );
}

type BillingActivityView = "invoices" | "charges";

function BillingActivityCard({ entityId }: { entityId: string }) {
  const t = useTranslations("settings.billing");
  const [view, setView] = useState<BillingActivityView>("invoices");

  return (
    <SettingCard
      title={t("activity.title")}
      description={t("activity.description")}
    >
      <div className="space-y-4">
        <div className="inline-flex rounded-lg bg-gray-100 p-1">
          <ViewTab
            active={view === "invoices"}
            Icon={DocumentTextIcon}
            label={t("activity.invoicesLabel")}
            onClick={() => setView("invoices")}
          />
          <ViewTab
            active={view === "charges"}
            Icon={ClockIcon}
            label={t("activity.chargesLabel")}
            onClick={() => setView("charges")}
          />
        </div>

        {view === "invoices" ? (
          <InvoiceTable
            id="settings-invoices"
            hiddenColumns={["payee"]}
            defaultEnabledStatuses={[
              InvoiceStatus.Issued,
              InvoiceStatus.Overdue,
              InvoiceStatus.Paid,
            ]}
            queryVariables={{ payeeContactId: entityId }}
          />
        ) : (
          <PaymentTable
            id={"settings-upcoming-payments"}
            searchable={false}
            columnToggles={false}
            hiddenColumns={[
              "payee",
              "status",
              "name",
              "fulfilled_by",
              "created_at",
            ]}
            queryVariables={{
              payeeContactId: entityId,
              status: [PaymentStatus.Unpaid],
            }}
          />
        )}
      </div>
    </SettingCard>
  );
}

function ViewTab({
  active,
  Icon,
  label,
  onClick,
}: {
  active: boolean;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
        active
          ? "bg-white text-gray-900 shadow-sm"
          : "text-gray-500 hover:text-gray-700",
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}
